import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { number, object } from 'yup';
// @mui
import {
  Alert,
  Box,
  Button,
  Card,
  DialogActions,
  Divider,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import { Iconify, Label } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import FormProvider from '@/components/hook-form/FormProvider';
import RHFTextField from '@/components/hook-form/RHFTextField';
import { SectionTitle } from '@/features/users/shared/components';
// api
import {
  deleteClientPaymentHoldRule,
  getClientPaymentHoldRule,
  updateClientPaymentHoldRule,
} from '@/features/users/shared/api';
// hooks
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
// types
import type {
  ClientPaymentHoldRule,
  ClientPaymentHoldRuleDto,
  User,
} from '@/features/users/shared/types';

type PaymentHoldRuleFormValues = {
  hold_days_before: number | string;
  notice_days_before_hold: number | string;
};

type ClientAutomationRulesProps = {
  client?: Pick<User, 'id' | 'payment_hold_rule'> | null;
  isLoading?: boolean;
};

type PaymentHoldRuleDialogProps = {
  open: boolean;
  rule: ClientPaymentHoldRule | null;
  isSubmitting: boolean;
  onClose: VoidFunction;
  onSubmit: (payload: ClientPaymentHoldRuleDto) => Promise<unknown>;
};

const DEFAULT_PAYMENT_HOLD_RULE_VALUES: PaymentHoldRuleFormValues = {
  hold_days_before: 1,
  notice_days_before_hold: 1,
};

const PAYMENT_HOLD_RULE_SCHEMA = object().shape({
  hold_days_before: number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .typeError('Hold days before session is required')
    .required('Hold days before session is required')
    .integer('Hold days before session must be a whole number')
    .min(0, 'Hold days before session must be at least 0')
    .max(365, 'Hold days before session must be 365 or fewer'),
  notice_days_before_hold: number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .typeError('Notify days before hold is required')
    .required('Notify days before hold is required')
    .integer('Notify days before hold must be a whole number')
    .min(1, 'Notify days before hold must be at least 1')
    .max(365, 'Notify days before hold must be 365 or fewer'),
});

const getPaymentHoldRuleFormValues = (
  rule?: ClientPaymentHoldRule | null
): PaymentHoldRuleFormValues => ({
  hold_days_before:
    rule?.hold_days_before ?? DEFAULT_PAYMENT_HOLD_RULE_VALUES.hold_days_before,
  notice_days_before_hold:
    rule?.notice_days_before_hold ??
    DEFAULT_PAYMENT_HOLD_RULE_VALUES.notice_days_before_hold,
});

const getPaymentHoldRulePayload = (
  values: PaymentHoldRuleFormValues
): ClientPaymentHoldRuleDto => ({
  hold_days_before: Number(values.hold_days_before),
  notice_days_before_hold: Number(values.notice_days_before_hold),
});

const formatDayCount = (days: number) =>
  `${days} ${days === 1 ? 'day' : 'days'}`;

function PaymentHoldRuleDialog({
  open,
  rule,
  isSubmitting,
  onClose,
  onSubmit,
}: PaymentHoldRuleDialogProps) {
  const methods = useForm<PaymentHoldRuleFormValues>({
    defaultValues: DEFAULT_PAYMENT_HOLD_RULE_VALUES,
    resolver: yupResolver(PAYMENT_HOLD_RULE_SCHEMA),
  });
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting: isFormSubmitting },
  } = methods;

  useEffect(() => {
    if (open) {
      reset(getPaymentHoldRuleFormValues(rule));
    }
  }, [open, reset, rule]);

  const isSaving = isSubmitting || isFormSubmitting;

  const handleClose = () => {
    if (isSaving) return;

    onClose();
  };

  const handleFormSubmit = async (values: PaymentHoldRuleFormValues) => {
    try {
      await onSubmit(getPaymentHoldRulePayload(values));
    } catch {
      // The mutation owns user-facing error handling and keeps the dialog open.
    }
  };

  return (
    <DialogSimple
      dialogTitle={`${rule ? 'Edit' : 'Enable'} custom payment hold`}
      open={open}
      onClickButtonClose={handleClose}
      loadingButton={false}
      disagreeButton={false}
      maxWidth="sm"
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing={2} sx={{ px: 3, pb: 1 }}>
          <Alert severity="info">
            This rule applies to all future sessions for this client and holds
            the remaining balance.
          </Alert>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <RHFTextField
              name="hold_days_before"
              label="Hold days before session"
              type="number"
              fullWidth
              inputProps={{
                min: 0,
                max: 365,
                step: 1,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">days</InputAdornment>
                ),
              }}
            />

            <RHFTextField
              name="notice_days_before_hold"
              label="Notify days before hold"
              type="number"
              fullWidth
              inputProps={{
                min: 1,
                max: 365,
                step: 1,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">days</InputAdornment>
                ),
              }}
            />
          </Stack>
        </Stack>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button color="inherit" disabled={isSaving} onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSaving}>
            Save rule
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </DialogSimple>
  );
}

export default function ClientAutomationRules({
  client,
  isLoading = false,
}: ClientAutomationRulesProps) {
  const { userId } = useParams();
  const queryClient = useQueryClient();
  const { canUpdate } = useGetAccess('clients');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const clientPaymentHoldRule = client?.payment_hold_rule;
  const clientId = userId ?? (client?.id ? String(client.id) : undefined);
  const parsedClientId = Number(clientId);
  const hasClientId = Boolean(clientId) && Number.isFinite(parsedClientId);
  const shouldFetchPaymentHoldRule = hasClientId && !client;
  const [paymentHoldRule, setPaymentHoldRule] =
    useState<ClientPaymentHoldRule | null>(
      client?.payment_hold_rule?.active === false
        ? null
        : client?.payment_hold_rule ?? null
    );
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);

  const { data: fetchedPaymentHoldRule, isLoading: isRuleLoading } = useQuery({
    queryKey: ['clientPaymentHoldRule', clientId],
    queryFn: () => getClientPaymentHoldRule(clientId as string),
    enabled: shouldFetchPaymentHoldRule,
    onError: (error) => {
      errorMsg(error, 'Unable to load custom payment hold rule');
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!client) return;

    setPaymentHoldRule(
      clientPaymentHoldRule?.active === false
        ? null
        : clientPaymentHoldRule ?? null
    );
  }, [client, clientPaymentHoldRule]);

  useEffect(() => {
    if (client || !shouldFetchPaymentHoldRule) return;

    setPaymentHoldRule(
      fetchedPaymentHoldRule?.active === false
        ? null
        : fetchedPaymentHoldRule ?? null
    );
  }, [client, fetchedPaymentHoldRule, shouldFetchPaymentHoldRule]);

  const syncClientPaymentHoldRule = (rule: ClientPaymentHoldRule | null) => {
    if (!clientId) return;

    queryClient.setQueryData<User | undefined>(
      ['clients', clientId],
      (currentClient) =>
        currentClient
          ? {
              ...currentClient,
              payment_hold_rule: rule,
            }
          : currentClient
    );
    queryClient.setQueryData<User | undefined>(
      ['clients', parsedClientId],
      (currentClient) =>
        currentClient
          ? {
              ...currentClient,
              payment_hold_rule: rule,
            }
          : currentClient
    );
    queryClient.setQueryData<ClientPaymentHoldRule | null>(
      ['clientPaymentHoldRule', clientId],
      rule
    );
  };

  const refreshClientPaymentHoldRule = () => {
    if (!clientId) return;

    queryClient.invalidateQueries(['clients']);
    queryClient.invalidateQueries(['clients', clientId]);
    queryClient.invalidateQueries(['clientPaymentHoldRule', clientId]);
  };

  const savePaymentHoldRuleMutation = useMutation({
    mutationFn: (payload: ClientPaymentHoldRuleDto) =>
      updateClientPaymentHoldRule(clientId as string, payload),
    onSuccess: (rule) => {
      setPaymentHoldRule(rule);
      syncClientPaymentHoldRule(rule);
      refreshClientPaymentHoldRule();
      setIsRuleDialogOpen(false);
      successMsg('Custom payment hold rule saved');
    },
    onError: (error) => {
      errorMsg(error, 'Unable to save custom payment hold rule');
    },
  });

  const deletePaymentHoldRuleMutation = useMutation({
    mutationFn: () => deleteClientPaymentHoldRule(clientId as string),
    onSuccess: () => {
      setPaymentHoldRule(null);
      syncClientPaymentHoldRule(null);
      refreshClientPaymentHoldRule();
      successMsg('Custom payment hold rule disabled');
    },
    onError: (error) => {
      errorMsg(error, 'Unable to disable custom payment hold rule');
    },
  });

  const handleOpenRuleDialog = () => {
    savePaymentHoldRuleMutation.reset();
    deletePaymentHoldRuleMutation.reset();
    setIsRuleDialogOpen(true);
  };

  const handleCloseRuleDialog = () => {
    if (savePaymentHoldRuleMutation.isLoading) return;

    setIsRuleDialogOpen(false);
  };

  const handleDisableRule = () => {
    if (!hasClientId || !paymentHoldRule) return;

    savePaymentHoldRuleMutation.reset();
    deletePaymentHoldRuleMutation.reset();
    deletePaymentHoldRuleMutation.mutate();
  };

  const mutationErrorMessage = savePaymentHoldRuleMutation.isError
    ? 'Unable to save this automation rule. Please check the values and try again.'
    : deletePaymentHoldRuleMutation.isError
    ? 'Unable to disable this automation rule. Please try again.'
    : null;
  const isPaymentHoldRuleLoading = shouldFetchPaymentHoldRule && isRuleLoading;
  const isBusy =
    savePaymentHoldRuleMutation.isLoading ||
    deletePaymentHoldRuleMutation.isLoading;

  if (!hasClientId) return null;

  if (isLoading || isPaymentHoldRuleLoading) {
    return (
      <Card sx={{ p: 3 }}>
        <SectionTitle title="Client Automation Rules" />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading automation rules...
        </Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Box>
          <SectionTitle title="Client Automation Rules" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage client-specific rules that apply to all future sessions for
            this client.
          </Typography>
        </Box>

        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 2.5,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
                bgcolor: 'primary.lighter',
              }}
            >
              <Iconify icon="eva:credit-card-outline" width={22} />
            </Box>

            <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Typography variant="subtitle1">Custom Payment Hold</Typography>
                <Label color={paymentHoldRule ? 'success' : 'default'}>
                  {paymentHoldRule ? 'Enabled' : 'Inactive'}
                </Label>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Place a payment hold before each upcoming session for clients
                with repeated payment issues.
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Use this for clients with repeated failed payments or
                insufficient funds on the cleaning day.
              </Typography>

              <Divider />

              {paymentHoldRule ? (
                <Stack spacing={0.75}>
                  <Typography variant="body2">
                    A payment hold will be placed{' '}
                    {formatDayCount(paymentHoldRule.hold_days_before)} before
                    each session.
                  </Typography>
                  <Typography variant="body2">
                    The client will be notified{' '}
                    {formatDayCount(paymentHoldRule.notice_days_before_hold)}{' '}
                    before the hold.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This applies to all future sessions for this client and
                    holds the remaining balance.
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={0.75}>
                  <Typography variant="body2" color="text.secondary">
                    No custom payment hold is configured for this client.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enable this rule to hold the remaining balance before every
                    future session for this client.
                  </Typography>
                </Stack>
              )}

              {mutationErrorMessage && (
                <Alert severity="error">{mutationErrorMessage}</Alert>
              )}
            </Stack>

            {canUpdate && (
              <Stack
                direction={{ xs: 'row', sm: 'column' }}
                spacing={1}
                sx={{ flexShrink: 0 }}
              >
                {paymentHoldRule ? (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<Iconify icon="eva:edit-outline" />}
                      disabled={isBusy}
                      onClick={handleOpenRuleDialog}
                    >
                      Edit
                    </Button>
                    <LoadingButton
                      variant="outlined"
                      color="error"
                      startIcon={<Iconify icon="eva:close-circle-outline" />}
                      loading={deletePaymentHoldRuleMutation.isLoading}
                      disabled={savePaymentHoldRuleMutation.isLoading}
                      onClick={handleDisableRule}
                    >
                      Disable rule
                    </LoadingButton>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    disabled={isBusy}
                    onClick={handleOpenRuleDialog}
                  >
                    Enable
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>

      <PaymentHoldRuleDialog
        open={isRuleDialogOpen}
        rule={paymentHoldRule}
        isSubmitting={savePaymentHoldRuleMutation.isLoading}
        onClose={handleCloseRuleDialog}
        onSubmit={(payload) => savePaymentHoldRuleMutation.mutateAsync(payload)}
      />
    </Card>
  );
}
